import { useEffect } from "react";
import { Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { updatePageSEO } from "@/lib/seo-canonical";
import { Home, Search, Phone, ArrowRight, FileQuestion } from "lucide-react";

const popularPages = [
  { name: "Home", path: "/", icon: Home },
  { name: "Services", path: "/services", icon: Search },
  { name: "Blog", path: "/blog", icon: FileQuestion },
  { name: "Contact", path: "/contact", icon: Phone },
];

export default function NotFound() {
  useEffect(() => {
    updatePageSEO({
      path: "/404",
      title: "Page Not Found | Integradyn",
      description: "The page you're looking for doesn't exist. Find what you need on our homepage or contact us for help."
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <span className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-[#00f0ff] to-[#b366ff] bg-clip-text text-transparent">
              404
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="text-404-title">
            Page Not Found
          </h1>
          
          <p className="text-lg text-gray-400 mb-8 max-w-md mx-auto" data-testid="text-404-description">
            Sorry, we couldn't find the page you're looking for. It may have been moved or no longer exists.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link href="/">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#00f0ff] to-[#b366ff] hover:opacity-90 text-white font-semibold gap-2"
                data-testid="button-go-home"
              >
                <Home className="w-5 h-5" />
                Go to Homepage
              </Button>
            </Link>
            <Link href="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-gray-600 text-white hover:bg-gray-800 gap-2"
                data-testid="button-contact-us"
              >
                <Phone className="w-5 h-5" />
                Contact Us
              </Button>
            </Link>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <h2 className="text-lg font-semibold text-white mb-4">
              Popular Pages
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {popularPages.map((page) => (
                <Link key={page.path} href={page.path}>
                  <Button
                    variant="ghost"
                    className="text-gray-400 hover:text-white hover:bg-gray-800 gap-2"
                    data-testid={`link-popular-${page.name.toLowerCase()}`}
                  >
                    <page.icon className="w-4 h-4" />
                    {page.name}
                    <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
